/**
 * ReflectionsPage Component
 *
 * Page for managing audio reflections.
 * Allows users to upload new reflections and view/delete existing ones.
 * Features search, filter, and sort functionality.
 * Design matches magicpatterns design spec with Processing/Ready states.
 */

import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Music,
  Trash2,
  Plus,
  ChevronRight,
  Search,
  SlidersHorizontal,
  Loader2,
  Calendar,
  Clock,
  CheckCircle2,
  RefreshCw,
  FileText,
  MoreVertical,
  AlertTriangle,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { NewReflectionModal } from "@/components/reflections/NewReflectionModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listReflections, deleteReflection } from "@/services/reflections";
import type { ReflectionResponse } from "@/types/reflection";

type SortOption = "date-desc" | "date-asc" | "title-asc" | "title-desc";

/**
 * Format duration in seconds to MM:SS format
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function ReflectionsPage() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("date-desc");
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reflectionToDelete, setReflectionToDelete] =
    useState<ReflectionResponse | null>(null);
  const queryClient = useQueryClient();

  // Check if any reflections are in a processing state
  const hasProcessingReflections = useCallback(
    (data: ReflectionResponse[] | undefined) => {
      if (!data) return false;
      return data.some(
        (r) =>
          r.status === "processing" ||
          r.status === "uploading" ||
          r.status === "uploaded"
      );
    },
    []
  );

  const {
    data: reflections,
    isLoading,
    error: loadError,
  } = useQuery<ReflectionResponse[], Error>({
    queryKey: ["reflections"],
    queryFn: () => listReflections(),
    // Poll every 3 seconds if there are any processing reflections
    refetchInterval: (query) =>
      hasProcessingReflections(query.state.data) ? 3000 : false,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReflection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reflections"] });
      setDeleteDialogOpen(false);
      setReflectionToDelete(null);
    },
  });

  // Filter and sort reflections
  const filteredReflections = useMemo(() => {
    if (!reflections) return [];

    let filtered = reflections.filter((r) => {
      const matchesSearch =
        r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return searchQuery === "" || matchesSearch;
    });

    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "date-desc":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "date-asc":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "title-asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title-desc":
          return (b.title || "").localeCompare(a.title || "");
        default:
          return 0;
      }
    });

    return filtered;
  }, [reflections, searchQuery, sortBy]);

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    setError(null);
    queryClient.invalidateQueries({ queryKey: ["reflections"] });
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleDeleteClick = (reflection: ReflectionResponse) => {
    setReflectionToDelete(reflection);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (reflectionToDelete) {
      deleteMutation.mutate(reflectionToDelete.id);
    }
  };

  const isProcessing = (status: string) =>
    status === "processing" || status === "uploading" || status === "uploaded";
  const isReady = (status: string) => status === "completed";

  return (
    <Layout>
      <div className="container mx-auto max-w-6xl px-4 py-8">
        {/* Header with New Reflection Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">
              Reflections
            </h1>
            <p className="text-muted-foreground">
              {filteredReflections.length}{" "}
              {filteredReflections.length === 1 ? "reflection" : "reflections"}
            </p>
          </div>
          <Button onClick={() => setShowUploadModal(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            New Reflection
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search reflections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </Button>
        </div>

        {showFilters && (
          <div className="mb-6 flex flex-wrap gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-1.5 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="title-asc">Title A-Z</option>
                <option value="title-desc">Title Z-A</option>
              </select>
            </div>
          </div>
        )}

        {isLoading && (
          <Card className="p-6">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">
                Loading reflections...
              </span>
            </div>
          </Card>
        )}

        {loadError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
            <p className="text-destructive">
              Error loading reflections: {loadError.message}
            </p>
          </div>
        )}

        {!isLoading && !loadError && filteredReflections.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? "No reflections match your search"
                : "No reflections yet"}
            </p>
            {!searchQuery && (
              <Button
                variant="link"
                className="mt-2"
                onClick={() => setShowUploadModal(true)}
              >
                Upload your first reflection
              </Button>
            )}
          </div>
        )}

        {!isLoading && filteredReflections.length > 0 && (
          <div className="space-y-4">
            {filteredReflections.map((reflection) => {
              const processing = isProcessing(reflection.status);
              const ready = isReady(reflection.status);
              const isClickable =
                ready ||
                reflection.status === "uploaded" ||
                (reflection.status === "processing" &&
                  reflection.recording_id !== null);

              const cardContent = (
                <div className="flex items-start gap-4 p-5">
                  {/* Music Icon */}
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-muted-foreground" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2">
                    {/* Title and Date/Duration Row */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground text-base">
                          {reflection.title || "Untitled Reflection"}
                        </h3>
                        {reflection.is_shared_copy && (
                          <Badge
                            variant="secondary"
                            className="bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 border-blue-200 dark:border-blue-800 text-xs"
                          >
                            Shared
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(
                            new Date(reflection.created_at),
                            "MM/dd/yyyy"
                          )}
                        </span>
                        {reflection.duration !== null &&
                          reflection.duration > 0 && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" />
                                {formatDuration(reflection.duration)}
                              </span>
                            </>
                          )}
                      </div>
                    </div>

                    {/* Description */}
                    {processing && (
                      <p className="text-sm text-muted-foreground">
                        Transcribing audio and generating insights...
                      </p>
                    )}
                    {ready && reflection.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {reflection.description}
                      </p>
                    )}

                    {/* Stats and Therapy Style */}
                    {ready && (
                      <div className="flex items-center gap-3 text-sm">
                        {reflection.insight_count > 0 && (
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {reflection.insight_count}
                            </span>{" "}
                            insights
                          </span>
                        )}
                        {reflection.segment_count > 0 && (
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {reflection.segment_count}
                            </span>{" "}
                            segments
                          </span>
                        )}
                        {reflection.therapy_style && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="secondary"
                                  className="bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900 uppercase text-xs font-medium max-w-[10rem]"
                                >
                                  <span className="truncate">
                                    {reflection.therapy_style.name}
                                  </span>
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{reflection.therapy_style.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status Badge, Menu, and Chevron */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {processing && (
                      <Badge
                        variant="secondary"
                        className="bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950 border-amber-200 dark:border-amber-800 gap-1.5"
                      >
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Processing
                      </Badge>
                    )}
                    {ready && (
                      <Badge
                        variant="secondary"
                        className="bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950 border-green-200 dark:border-green-800 gap-1.5"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Ready
                      </Badge>
                    )}
                    {reflection.status === "failed" && (
                      <Badge variant="destructive">Failed</Badge>
                    )}

                    {/* Three-dot Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => e.preventDefault()}
                          aria-label="More options"
                        >
                          <MoreVertical className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteClick(reflection);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Reflection
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Chevron */}
                    {isClickable && (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              );

              return (
                <Card
                  key={reflection.id}
                  className="overflow-hidden hover:shadow-md transition-shadow"
                >
                  {isClickable ? (
                    <Link
                      to={`/reflections/${reflection.id}`}
                      className="block"
                    >
                      {cardContent}
                    </Link>
                  ) : (
                    cardContent
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -mt-2 -mr-2"
                onClick={() => setDeleteDialogOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <AlertDialogTitle className="text-xl">
              Delete Reflection?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">
                "{reflectionToDelete?.title || "Untitled Reflection"}"
              </span>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Warning Box */}
          <div className="bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 rounded-lg p-4 mt-2">
            <p className="font-medium text-red-800 dark:text-red-200 mb-2">
              This action cannot be undone.
            </p>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 list-disc list-inside">
              <li>All transcript data will be permanently deleted</li>
              <li>All insights and analysis will be lost</li>
              <li>All comments and annotations will be removed</li>
              <li>The source audio recording will be fully deleted</li>
            </ul>
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 text-white"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete Permanently"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Reflection Modal */}
      <NewReflectionModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onUploadComplete={handleUploadComplete}
        onUploadError={handleUploadError}
      />
    </Layout>
  );
}
